/** GA4 measurement ID - matches index.html; override via VITE_GA_MEASUREMENT_ID. */
const GA_MEASUREMENT_ID =
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() || 'G-S7635W0XJ3';

export type AnalyticsEventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]) {
  if (typeof window === 'undefined') return;
  window.gtag?.(...args);
}

/** Fire a custom GA4 event. No-op when gtag is unavailable (SSR/tests). */
export function trackEvent(name: string, params?: AnalyticsEventParams) {
  gtag('event', name, params);
}

/** SPA route change - send page_view with path + query. */
export function trackPageView(path: string, title?: string) {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? (typeof document !== 'undefined' ? document.title : undefined),
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

/** Tie portal sessions to a stable user id (Supabase auth uid). */
export function setAnalyticsUserId(userId: string | null) {
  gtag('config', GA_MEASUREMENT_ID, { user_id: userId ?? undefined });
}

export function inferCtaName(destination: string): string {
  if (destination.includes('khata-calculator')) return 'open_khata_calculator';
  if (destination.includes('drive/gba/apply')) return 'apply_b_khata';
  if (destination === '/portal' || destination.startsWith('/portal?')) return 'open_portal';
  if (destination.startsWith('/portal/auth')) return 'open_portal_auth';
  if (destination === '#contact') return 'contact_form';
  return 'other';
}

export function trackCtaClick(params: {
  cta_name?: string;
  cta_label: string;
  cta_location: string;
  destination: string;
}) {
  trackEvent('cta_click', {
    cta_name: params.cta_name ?? inferCtaName(params.destination),
    cta_label: params.cta_label,
    cta_location: params.cta_location,
    destination: params.destination,
  });
}

export function trackGenerateLead(params: {
  lead_type: string;
  service: string;
  form_location?: string;
}) {
  trackEvent('generate_lead', {
    lead_type: params.lead_type,
    service: params.service,
    form_location: params.form_location ?? 'homepage_contact',
  });
}

export function trackSignUp(params: { method?: string; user_type: 'customer' | 'partner' }) {
  trackEvent('sign_up', {
    method: params.method ?? 'email_otp',
    user_type: params.user_type,
  });
}

export function trackLogin(params?: { method?: string }) {
  trackEvent('login', {
    method: params?.method ?? 'email',
  });
}

export function trackSubmitApplication(params: {
  application_type: string;
  floor_tier?: string;
  value?: number;
  currency?: string;
}) {
  trackEvent('submit_application', {
    application_type: params.application_type,
    floor_tier: params.floor_tier,
    value: params.value,
    currency: params.currency ?? 'INR',
  });
}

export function trackClickCall(params: { location: string }) {
  trackEvent('click_call', { location: params.location });
}

export function trackClickWhatsApp(params: { location: string }) {
  trackEvent('click_whatsapp', { location: params.location });
}
